/* ═══════════════════════════════════════════════════════════════
   MOTOR UNIFICADO DE PERSONAS — Admin Radio
   Fuente canónica única para Hosts, Staff, Community, Analytics,
   Live, Schedule, Cabina y Chat.

   Principios:
   - Una persona = un ID canónico (p-NNN)
   - Múltiples roles por persona (host, staff, community_member...)
   - Los mocks legacy (adminHosts, adminStaff, etc.) son ADAPTADORES
     que leen de acá y exportan en su formato antiguo.
   ═══════════════════════════════════════════════════════════════ */

/* ─── Tipos base ─── */
export type PersonRole = 'admin' | 'host' | 'staff_moderator' | 'staff_marketing' | 'staff_sales' | 'staff_guest' | 'community_member' | 'listener';

export type PersonStatus = 'active' | 'inactive' | 'suspended' | 'invited';

/* ─── Perfil de Locutor (Host) ─── */
export interface PersonHostProfile {
  username: string;
  programs: string[];           // program IDs (p-slug)
  followers: number;
  interactions: { likes: number; comments: number; shares: number };
  monthlyRevenue: number;
  projectedRevenue: number;
  joinedAt: string;            // ISO
  hostStatus: 'on_air' | 'active' | 'inactive';
}

/* ─── Perfil de Staff ─── */
export type StaffRole = 'administrador' | 'locutor' | 'moderador' | 'marketing' | 'ventas' | 'invitado';
export type StaffPermission = 'cabina' | 'chat' | 'contenido' | 'monetizacion' | 'analytics' | 'configuracion';

export interface PersonStaffProfile {
  role: StaffRole;
  permissions: StaffPermission[];
  socialLinks: {
    instagram?: string;
    twitter?: string;
    youtube?: string;
    tiktok?: string;
    website?: string;
  };
  assignedPrograms: string[];  // program names (display)
  joinedAt: string;
  staffStatus: 'active' | 'inactive' | 'invited' | 'suspended';
  invitedAt?: string;
  suspendedAt?: string;
  suspensionReason?: string;
}

/* ─── Perfil de Comunidad ─── */
export type CommunityTier = 'seguidor' | 'topfan' | 'vip' | 'embajador';

export interface PersonCommunityProfile {
  username: string;
  country: string;
  countryFlag: string;
  tier: CommunityTier;
  interactions: number;
  lastActive: string;
  joinedAt: string;
  topFanRank?: number;
  topFanBadge?: 'oro' | 'plata' | 'bronce';
  vipSince?: string;
  vipLevel?: 1 | 2 | 3;
  embajadorRegion?: string;
  embajadorSince?: string;
  embajadorLogros?: string[];
}

/* ─── Persona Canónica ─── */
export interface CanonicalPerson {
  id: string;                   // "p-001"
  firstName: string;
  lastName: string;
  displayName: string;
  email: string;
  phone?: string;
  avatar: string;              // URL de imagen
  bio: string;
  status: PersonStatus;
  roles: PersonRole[];
  hostProfile?: PersonHostProfile;
  staffProfile?: PersonStaffProfile;
  communityProfile?: PersonCommunityProfile;
  /* Aliases usados en mocks legacy (para mapeo) */
  legacyAliases: string[];
  createdAt: string;
  updatedAt: string;
}

/* ═══════════════════════════════════════════════════════════════
   PERSONAS CANÓNICAS (~22)
   ═══════════════════════════════════════════════════════════════ */

export const canonicalPersons: CanonicalPerson[] = [
  /* ── 1. Carlos Mendoza — Admin, Host, Staff(admin) ── */
  {
    id: 'p-001',
    firstName: 'Carlos',
    lastName: 'Mendoza',
    displayName: 'Carlos Mendoza',
    email: 'carlos@radiofrecuenciaweb.com',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20Latino%20male%20radio%20station%20owner%20in%20his%2040s%2C%20warm%20confident%20expression%2C%20casual%20business%20shirt%2C%20studio%20lighting%2C%20clean%20background%2C%20editorial%20portrait%20photography&width=160&height=160&seq=person-carlos-mendoza&orientation=squarish',
    bio: 'Fundador de la emisora. Más de 15 años en radio. Apasionado por la música latina.',
    status: 'active',
    roles: ['admin', 'host'],
    hostProfile: {
      username: '@cmendoza',
      programs: ['entrevistas-exclusivas', 'noticias-al-instante'],
      followers: 31200,
      interactions: { likes: 119800, comments: 24600, shares: 12000 },
      monthlyRevenue: 1750000,
      projectedRevenue: 2100000,
      joinedAt: '2024-01-15T10:00:00Z',
      hostStatus: 'active',
    },
    staffProfile: {
      role: 'administrador',
      permissions: ['cabina', 'chat', 'contenido', 'monetizacion', 'analytics', 'configuracion'],
      socialLinks: { instagram: '@carlosmendoza', twitter: '@carlosmendoza' },
      assignedPrograms: ['El Mañanero', 'Entrevistas Exclusivas'],
      joinedAt: '2024-01-15T10:00:00Z',
      staffStatus: 'active',
    },
    legacyAliases: ['Carlos Mendoza', 'Carlos Méndez'],
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2026-06-19T10:00:00Z',
  },

  /* ── 2. Valentina Ríos — Host, Staff(locutor). Alias: Valentina Herrera (Schedule), Valentina Suárez (Community) ── */
  {
    id: 'p-002',
    firstName: 'Valentina',
    lastName: 'Ríos',
    displayName: 'Valentina Ríos',
    email: 'valentina@radiofrecuenciaweb.com',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20Latina%20female%20radio%20host%20in%20her%20early%2030s%2C%20warm%20confident%20smile%2C%20stylish%20casual%20chic%20outfit%2C%20soft%20studio%20lighting%20with%20cream%20background%2C%20editorial%20headshot%20photography%2C%20natural%20makeup%2C%20approachable%20and%20charismatic%20expression&width=400&height=400&seq=host-valentina-01&orientation=squarish',
    bio: 'Host principal del programa matutino "El Mañanero". Especialista en entretenimiento y cultura digital. Más de 8 años en radio.',
    status: 'active',
    roles: ['host'],
    hostProfile: {
      username: '@valerios',
      programs: ['el-mananero', 'top-40-countdown'],
      followers: 48500,
      interactions: { likes: 128400, comments: 32100, shares: 45600 },
      monthlyRevenue: 1850000,
      projectedRevenue: 2200000,
      joinedAt: '2024-03-01T10:00:00Z',
      hostStatus: 'active',
    },
    staffProfile: {
      role: 'locutor',
      permissions: ['cabina', 'chat', 'contenido', 'analytics'],
      socialLinks: { instagram: '@valerios', twitter: '@valerios', tiktok: '@valerios_oficial' },
      assignedPrograms: ['El Mañanero', 'Top 40 Countdown'],
      joinedAt: '2024-03-01T10:00:00Z',
      staffStatus: 'active',
    },
    legacyAliases: ['Valentina Ríos', 'Valentina Herrera', 'Valentina Suárez'],
    createdAt: '2024-03-01T10:00:00Z',
    updatedAt: '2026-06-19T10:00:00Z',
  },

  /* ── 3. Roberto Aguilar — Host, Staff(locutor). Alias: Roberto Díaz (Community) ── */
  {
    id: 'p-003',
    firstName: 'Roberto',
    lastName: 'Aguilar',
    displayName: 'Roberto Aguilar',
    email: 'roberto@radiofrecuenciaweb.com',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20Latino%20male%20sports%20radio%20host%20in%20his%20mid%2030s%2C%20energetic%20and%20passionate%20expression%2C%20casual%20polo%20shirt%2C%20warm%20studio%20backdrop%2C%20editorial%20portrait%20photography%2C%20confident%20masculine%20look%2C%20clean%20background&width=400&height=400&seq=host-roberto-02&orientation=squarish',
    bio: 'Host deportivo. Cubre fútbol nacional e internacional, béisbol y eventos en vivo. 10 años de trayectoria en medios.',
    status: 'active',
    roles: ['host'],
    hostProfile: {
      username: '@robertoaguilar',
      programs: ['zona-deportiva'],
      followers: 38900,
      interactions: { likes: 98700, comments: 28300, shares: 39200 },
      monthlyRevenue: 1520000,
      projectedRevenue: 1850000,
      joinedAt: '2024-07-12T10:00:00Z',
      hostStatus: 'on_air',
    },
    staffProfile: {
      role: 'locutor',
      permissions: ['cabina', 'chat', 'contenido', 'analytics'],
      socialLinks: { instagram: '@robertoaguilar', twitter: '@robertoaguilar' },
      assignedPrograms: ['Zona Deportiva'],
      joinedAt: '2024-07-12T10:00:00Z',
      staffStatus: 'active',
    },
    legacyAliases: ['Roberto Aguilar', 'Roberto Díaz'],
    createdAt: '2024-07-12T10:00:00Z',
    updatedAt: '2026-06-19T10:00:00Z',
  },

  /* ── 4. Andrés Castillo — Host, Staff(locutor). Alias: Andrés Vega (Community) ── */
  {
    id: 'p-004',
    firstName: 'Andrés',
    lastName: 'Castillo',
    displayName: 'Andrés Castillo',
    email: 'andres@radiofrecuenciaweb.com',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20Latino%20male%20radio%20DJ%20in%20his%20late%2020s%2C%20cool%20confident%20look%2C%20denim%20jacket%2C%20moody%20atmospheric%20studio%20lighting%2C%20modern%20editorial%20portrait%2C%20urban%20creative%20vibe%2C%20clean%20dark%20background&width=400&height=400&seq=host-andres-03&orientation=squarish',
    bio: 'DJ de la tarde. Experto en música electrónica, hip-hop e indie. Productor musical y creador de contenido.',
    status: 'active',
    roles: ['host'],
    hostProfile: {
      username: '@andrescastillo',
      programs: ['la-hora-pico', 'beats-and-bytes'],
      followers: 52100,
      interactions: { likes: 142600, comments: 35100, shares: 52100 },
      monthlyRevenue: 1980000,
      projectedRevenue: 2400000,
      joinedAt: '2024-04-10T10:00:00Z',
      hostStatus: 'active',
    },
    staffProfile: {
      role: 'locutor',
      permissions: ['cabina', 'chat', 'contenido', 'analytics'],
      socialLinks: { instagram: '@andrescastillo', youtube: '@andrescastillo' },
      assignedPrograms: ['La Hora Pico', 'Beats & Bytes'],
      joinedAt: '2024-04-10T10:00:00Z',
      staffStatus: 'active',
    },
    legacyAliases: ['Andrés Castillo', 'Andrés Vega'],
    createdAt: '2024-04-10T10:00:00Z',
    updatedAt: '2026-06-19T10:00:00Z',
  },

  /* ── 5. Camila Ortega — Host, Staff(locutor) ── */
  {
    id: 'p-005',
    firstName: 'Camila',
    lastName: 'Ortega',
    displayName: 'Camila Ortega',
    email: 'camila@radiofrecuenciaweb.com',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20Latina%20female%20DJ%20in%20her%20mid%2020s%2C%20edgy%20cool%20style%2C%20headphones%20around%20neck%2C%20neon%20ambient%20lighting%2C%20modern%20music%20scene%20editorial%20photography%2C%20confident%20expression%2C%20creative%20artistic%20vibe&width=400&height=400&seq=host-camila-04&orientation=squarish',
    bio: 'DJ nocturna especializada en house, techno y sesiones en vivo. Energía pura en cabina. También productora.',
    status: 'active',
    roles: ['host'],
    hostProfile: {
      username: '@camilaortega',
      programs: ['sesiones-acusticas'],
      followers: 31200,
      interactions: { likes: 87600, comments: 19800, shares: 34500 },
      monthlyRevenue: 1250000,
      projectedRevenue: 1600000,
      joinedAt: '2025-02-01T10:00:00Z',
      hostStatus: 'active',
    },
    staffProfile: {
      role: 'locutor',
      permissions: ['cabina', 'chat', 'contenido', 'analytics'],
      socialLinks: { instagram: '@camilaortega', tiktok: '@camilaortega_dj', website: 'camilaortega.dj' },
      assignedPrograms: ['Beats & Bytes', 'Sesiones Acústicas'],
      joinedAt: '2025-02-01T10:00:00Z',
      staffStatus: 'invited',
      invitedAt: '2026-06-16T10:00:00Z',
    },
    legacyAliases: ['Camila Ortega'],
    createdAt: '2025-02-01T10:00:00Z',
    updatedAt: '2026-06-19T10:00:00Z',
  },

  /* ── 6. Sofía Linares — Host, Staff(marketing). Alias: Sofía Herrera (Analytics) ── */
  {
    id: 'p-006',
    firstName: 'Sofía',
    lastName: 'Linares',
    displayName: 'Sofía Linares',
    email: 'sofia@radiofrecuenciaweb.com',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20Latina%20female%20radio%20host%20in%20her%20mid%2020s%2C%20trendy%20Gen%20Z%20style%2C%20confident%20smile%2C%20bright%20colorful%20creative%20background%2C%20modern%20editorial%20headshot%20photography%2C%20youthful%20energetic%20expression%2C%20natural%20makeup&width=400&height=400&seq=host-sofia-07&orientation=squarish',
    bio: 'Creadora de contenido digital. Maneja Instagram, TikTok y YouTube de la radio. Voz fresca de la nueva generación.',
    status: 'active',
    roles: ['host', 'staff_marketing'],
    hostProfile: {
      username: '@sofialinares',
      programs: ['trend-alert'],
      followers: 42100,
      interactions: { likes: 115300, comments: 29800, shares: 48700 },
      monthlyRevenue: 1620000,
      projectedRevenue: 2100000,
      joinedAt: '2025-08-01T10:00:00Z',
      hostStatus: 'on_air',
    },
    staffProfile: {
      role: 'marketing',
      permissions: ['contenido', 'analytics'],
      socialLinks: { instagram: '@sofialinares', tiktok: '@sofialinares', youtube: '@sofialinares' },
      assignedPrograms: ['Top 40 Countdown'],
      joinedAt: '2024-09-01T10:00:00Z',
      staffStatus: 'active',
    },
    legacyAliases: ['Sofía Linares', 'Sofía Herrera'],
    createdAt: '2024-09-01T10:00:00Z',
    updatedAt: '2026-06-19T10:00:00Z',
  },

  /* ── 7. Javier Solís — Host, Staff(moderador) ── */
  {
    id: 'p-007',
    firstName: 'Javier',
    lastName: 'Solís',
    displayName: 'Javier Solís',
    email: 'javier@radiofrecuenciaweb.com',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20Latino%20male%20radio%20host%20in%20his%20early%2030s%2C%20warm%20friendly%20expression%2C%20casual%20smart%20shirt%2C%20bright%20modern%20studio%20background%2C%20clean%20editorial%20portrait%20photography%2C%20trustworthy%20and%20approachable%20look%2C%20soft%20natural%20lighting&width=400&height=400&seq=host-javier-08&orientation=squarish',
    bio: 'Host de noticias y actualidad. Periodista con 12 años de experiencia. Moderador de comunidad.',
    status: 'active',
    roles: ['host', 'staff_moderator'],
    hostProfile: {
      username: '@javiersolis',
      programs: ['noticias-al-dia', 'debate-abierto'],
      followers: 29300,
      interactions: { likes: 78200, comments: 22100, shares: 28900 },
      monthlyRevenue: 1350000,
      projectedRevenue: 1550000,
      joinedAt: '2025-03-10T10:00:00Z',
      hostStatus: 'active',
    },
    staffProfile: {
      role: 'moderador',
      permissions: ['cabina', 'chat'],
      socialLinks: { twitter: '@javiersolis' },
      assignedPrograms: ['El Mañanero', 'La Hora Pico', 'Top 40 Countdown'],
      joinedAt: '2025-04-15T10:00:00Z',
      staffStatus: 'active',
    },
    legacyAliases: ['Javier Solís'],
    createdAt: '2025-03-10T10:00:00Z',
    updatedAt: '2026-06-19T10:00:00Z',
  },

  /* ── 8. Mariana Solano — Host only (inactive) ── */
  {
    id: 'p-008',
    firstName: 'Mariana',
    lastName: 'Solano',
    displayName: 'Mariana Solano',
    email: 'mariana@radiofrecuenciaweb.com',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20Latina%20female%20radio%20host%20in%20her%20early%2030s%2C%20intellectual%20warm%20expression%2C%20chic%20glasses%2C%20creative%20artsy%20background%2C%20modern%20editorial%20portrait%20photography%2C%20cultured%20sophisticated%20look%2C%20soft%20natural%20lighting&width=400&height=400&seq=host-mariana-05&orientation=squarish',
    bio: 'Host cultural y entrevistadora. Especialista en cine, literatura y artes. Columnista en medios digitales.',
    status: 'inactive',
    roles: ['host'],
    hostProfile: {
      username: '@marianasolano',
      programs: ['entrevistas-exclusivas', 'cultura-viva'],
      followers: 24600,
      interactions: { likes: 65400, comments: 18200, shares: 22300 },
      monthlyRevenue: 980000,
      projectedRevenue: 1100000,
      joinedAt: '2025-05-20T10:00:00Z',
      hostStatus: 'inactive',
    },
    legacyAliases: ['Mariana Solano'],
    createdAt: '2025-05-20T10:00:00Z',
    updatedAt: '2026-06-19T10:00:00Z',
  },

  /* ── 9. Diego Montenegro — Host only ── */
  {
    id: 'p-009',
    firstName: 'Diego',
    lastName: 'Montenegro',
    displayName: 'Diego Montenegro',
    email: 'diego.montenegro@radiofrecuenciaweb.com',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20Latino%20male%20radio%20host%20in%20his%20late%2020s%2C%20friendly%20charismatic%20smile%2C%20casual%20smart%20style%2C%20bright%20airy%20studio%20background%2C%20modern%20editorial%20portrait%2C%20approachable%20and%20energetic%20expression%2C%20natural%20lighting&width=400&height=400&seq=host-diego-06&orientation=squarish',
    bio: 'Host de fin de semana. Especialista en música urbana, reggaetón y trap latino. Conductor de eventos en vivo.',
    status: 'active',
    roles: ['host'],
    hostProfile: {
      username: '@diegomontenegro',
      programs: ['urban-beats'],
      followers: 35700,
      interactions: { likes: 91200, comments: 24600, shares: 38900 },
      monthlyRevenue: 1420000,
      projectedRevenue: 1750000,
      joinedAt: '2025-01-15T10:00:00Z',
      hostStatus: 'active',
    },
    legacyAliases: ['Diego Montenegro'],
    createdAt: '2025-01-15T10:00:00Z',
    updatedAt: '2026-06-19T10:00:00Z',
  },

  /* ── 10. María José Herrera — Staff(marketing). Alias: María Gutiérrez (Analytics) ── */
  {
    id: 'p-010',
    firstName: 'María José',
    lastName: 'Herrera',
    displayName: 'María José Herrera',
    email: 'mariajose@radiofrecuenciaweb.com',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20Latina%20female%20marketing%20manager%20in%20her%2030s%2C%20creative%20focused%20expression%2C%20chic%20glasses%2C%20modern%20office%20background%2C%20editorial%20photography&width=160&height=160&seq=staff-mariajose-v2&orientation=squarish',
    bio: 'Manager de marketing y redes sociales. Coordina campañas, publicidad y contenido digital.',
    status: 'active',
    roles: ['staff_marketing'],
    staffProfile: {
      role: 'marketing',
      permissions: ['contenido', 'analytics'],
      socialLinks: { instagram: '@mjherrera', twitter: '@mjherrera' },
      assignedPrograms: ['El Mañanero', 'Top 40 Countdown'],
      joinedAt: '2024-02-20T10:00:00Z',
      staffStatus: 'active',
    },
    legacyAliases: ['María José Herrera', 'María Gutiérrez'],
    createdAt: '2024-02-20T10:00:00Z',
    updatedAt: '2026-06-19T10:00:00Z',
  },

  /* ── 11. Diego Fuentes — Staff(moderador, suspended). NO confundir con Diego Montenegro ni Diego Ramírez ── */
  {
    id: 'p-011',
    firstName: 'Diego',
    lastName: 'Fuentes',
    displayName: 'Diego Fuentes',
    email: 'diego@radiofrecuenciaweb.com',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20Latino%20male%20community%20moderator%20in%20his%20mid%2020s%2C%20approachable%20friendly%20face%2C%20casual%20hoodie%2C%20modern%20tech%20office%2C%20clean%20portrait%20photography&width=160&height=160&seq=staff-diego-v2&orientation=squarish',
    bio: 'Moderador de cabina y comunidad. Mantiene el chat limpio durante las transmisiones.',
    status: 'suspended',
    roles: ['staff_moderator'],
    staffProfile: {
      role: 'moderador',
      permissions: ['cabina', 'chat'],
      socialLinks: { twitter: '@diegofuentes' },
      assignedPrograms: ['El Mañanero', 'La Hora Pico'],
      joinedAt: '2024-05-15T10:00:00Z',
      staffStatus: 'suspended',
      suspendedAt: '2026-05-20T10:00:00Z',
      suspensionReason: 'Inactividad prolongada en cabina. No responde a mensajes del equipo.',
    },
    legacyAliases: ['Diego Fuentes'],
    createdAt: '2024-05-15T10:00:00Z',
    updatedAt: '2026-06-19T10:00:00Z',
  },

  /* ── 12. Lucía Montenegro — Staff(ventas). Co-host en Programs ── */
  {
    id: 'p-012',
    firstName: 'Lucía',
    lastName: 'Montenegro',
    displayName: 'Lucía Montenegro',
    email: 'lucia@radiofrecuenciaweb.com',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20Latina%20female%20sales%20representative%20in%20her%20late%2020s%2C%20confident%20business%20smile%2C%20casual%20blazer%2C%20bright%20modern%20office%2C%20editorial%20portrait&width=160&height=160&seq=staff-lucia-v2&orientation=squarish',
    bio: 'Representante de ventas publicitarias. Conecta anunciantes con la radio y gestiona patrocinios.',
    status: 'active',
    roles: ['staff_sales'],
    staffProfile: {
      role: 'ventas',
      permissions: ['monetizacion', 'analytics'],
      socialLinks: { instagram: '@luciamontenegro', website: 'luciamontenegro.com' },
      assignedPrograms: [],
      joinedAt: '2024-06-01T10:00:00Z',
      staffStatus: 'active',
    },
    legacyAliases: ['Lucía Montenegro'],
    createdAt: '2024-06-01T10:00:00Z',
    updatedAt: '2026-06-19T10:00:00Z',
  },

  /* ── 13. Pedro Navarro — Staff(invitado) ── */
  {
    id: 'p-013',
    firstName: 'Pedro',
    lastName: 'Navarro',
    displayName: 'Pedro Navarro',
    email: 'pedro@radiofrecuenciaweb.com',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20Latino%20male%20guest%20collaborator%20in%20his%20late%2020s%2C%20friendly%20helpful%20expression%2C%20casual%20tech%20shirt%2C%20bright%20background%2C%20modern%20portrait&width=160&height=160&seq=staff-pedro-v2&orientation=squarish',
    bio: 'Productor invitado. Colabora en sesiones especiales y eventos en vivo.',
    status: 'invited',
    roles: ['staff_guest'],
    staffProfile: {
      role: 'invitado',
      permissions: [],
      socialLinks: {},
      assignedPrograms: ['Sesiones Acústicas'],
      joinedAt: '2025-01-10T10:00:00Z',
      staffStatus: 'invited',
      invitedAt: '2026-06-15T14:00:00Z',
    },
    legacyAliases: ['Pedro Navarro'],
    createdAt: '2025-01-10T10:00:00Z',
    updatedAt: '2026-06-19T10:00:00Z',
  },

  /* ── 14. Andrea Castillo — Schedule host (NO es Andrés Castillo, es persona distinta) ── */
  {
    id: 'p-014',
    firstName: 'Andrea',
    lastName: 'Castillo',
    displayName: 'Andrea Castillo',
    email: 'andrea@radiofrecuenciaweb.com',
    avatar: 'https://readdy.ai/api/search-image?query=Cheerful%20young%20Hispanic%20female%20radio%20presenter%20with%20bright%20smile%2C%20casual%20modern%20attire%2C%20vibrant%20studio%20background%20with%20colorful%20lighting%2C%20energetic%20and%20warm%20media%20portrait%20photography&width=80&height=80&seq=sched-host-andrea&orientation=squarish',
    bio: 'Locutora matutina. Especialista en programación familiar y contenido para todo público.',
    status: 'active',
    roles: ['host'],
    hostProfile: {
      username: '@andreacastillo',
      programs: ['despierta-con-energia', 'domingo-familiar'],
      followers: 18900,
      interactions: { likes: 54200, comments: 12300, shares: 9800 },
      monthlyRevenue: 980000,
      projectedRevenue: 1200000,
      joinedAt: '2024-06-01T10:00:00Z',
      hostStatus: 'active',
    },
    legacyAliases: ['Andrea Castillo'],
    createdAt: '2024-06-01T10:00:00Z',
    updatedAt: '2026-06-19T10:00:00Z',
  },

  /* ── 15. DJ Marco — Schedule host ── */
  {
    id: 'p-015',
    firstName: 'Marco',
    lastName: 'Rojas',
    displayName: 'DJ Marco',
    email: 'djmarco@radiofrecuenciaweb.com',
    avatar: 'https://readdy.ai/api/search-image?query=Male%20DJ%20portrait%20with%20stylish%20urban%20look%2C%20confident%20expression%2C%20wearing%20trendy%20streetwear%2C%20nightclub%20atmosphere%20background%20with%20colorful%20lights%2C%20creative%20music%20industry%20portrait%20photography&width=80&height=80&seq=sched-host-djmarco&orientation=squarish',
    bio: 'DJ y productor. Especialista en música electrónica y conciertos en vivo.',
    status: 'active',
    roles: ['host'],
    hostProfile: {
      username: '@djmarco',
      programs: ['miercoles-de-conciertos', 'noche-de-electronica'],
      followers: 22100,
      interactions: { likes: 67400, comments: 15200, shares: 18400 },
      monthlyRevenue: 1350000,
      projectedRevenue: 1600000,
      joinedAt: '2024-08-15T10:00:00Z',
      hostStatus: 'active',
    },
    legacyAliases: ['DJ Marco'],
    createdAt: '2024-08-15T10:00:00Z',
    updatedAt: '2026-06-19T10:00:00Z',
  },

  /* ── 16. Memo López — Schedule host (deportes) ── */
  {
    id: 'p-016',
    firstName: 'Guillermo',
    lastName: 'López',
    displayName: 'Memo López',
    email: 'memo@radiofrecuenciaweb.com',
    avatar: 'https://readdy.ai/api/search-image?query=Intelligent%20male%20sports%20journalist%20portrait%20with%20analytical%20look%2C%20wearing%20casual%20smart%20attire%2C%20glasses%2C%20professional%20office%20background%20with%20sports%20screens%2C%20media%20industry%20headshot%20photography&width=80&height=80&seq=sched-host-memo&orientation=squarish',
    bio: 'Periodista deportivo. Análisis, estadísticas y cobertura de eventos en vivo.',
    status: 'active',
    roles: ['host'],
    hostProfile: {
      username: '@memolopez',
      programs: ['deportes-de-noche'],
      followers: 15200,
      interactions: { likes: 45300, comments: 12800, shares: 8700 },
      monthlyRevenue: 850000,
      projectedRevenue: 1050000,
      joinedAt: '2025-02-01T10:00:00Z',
      hostStatus: 'active',
    },
    legacyAliases: ['Memo López'],
    createdAt: '2025-02-01T10:00:00Z',
    updatedAt: '2026-06-19T10:00:00Z',
  },

  /* ── 17. Mariana Vega — Live host ── */
  {
    id: 'p-017',
    firstName: 'Mariana',
    lastName: 'Vega',
    displayName: 'Mariana Vega',
    email: 'mariana.vega@radiofrecuenciaweb.com',
    avatar: 'https://readdy.ai/api/search-image?query=Young%20Latina%20female%20radio%20host%20with%20bright%20energetic%20smile%2C%20colorful%20casual%20style%2C%20headphones%20visible%2C%20modern%20broadcast%20studio%2C%20vibrant%20creative%20portrait%20photography&width=160&height=160&seq=person-mariana-vega&orientation=squarish',
    bio: 'Host de transmisiones en vivo y eventos especiales. Energía pura frente al micrófono.',
    status: 'active',
    roles: ['host'],
    hostProfile: {
      username: '@marianavega',
      programs: [],
      followers: 12400,
      interactions: { likes: 31200, comments: 7800, shares: 5600 },
      monthlyRevenue: 720000,
      projectedRevenue: 900000,
      joinedAt: '2025-06-01T10:00:00Z',
      hostStatus: 'active',
    },
    legacyAliases: ['Mariana Vega'],
    createdAt: '2025-06-01T10:00:00Z',
    updatedAt: '2026-06-19T10:00:00Z',
  },

  /* ── 18. Natalia Ruiz — Live host ── */
  {
    id: 'p-018',
    firstName: 'Natalia',
    lastName: 'Ruiz',
    displayName: 'Natalia Ruiz',
    email: 'natalia@radiofrecuenciaweb.com',
    avatar: 'https://readdy.ai/api/search-image?query=Young%20Latina%20female%20radio%20presenter%20with%20warm%20engaging%20smile%2C%20modern%20casual%20look%2C%20bright%20studio%20environment%2C%20approachable%20media%20portrait%20photography&width=160&height=160&seq=person-natalia-ruiz&orientation=squarish',
    bio: 'Host de lives y cobertura de eventos. Conduce transmisiones especiales desde exteriores.',
    status: 'active',
    roles: ['host'],
    hostProfile: {
      username: '@nataliaruiz',
      programs: [],
      followers: 9800,
      interactions: { likes: 24500, comments: 6200, shares: 4800 },
      monthlyRevenue: 650000,
      projectedRevenue: 800000,
      joinedAt: '2025-08-01T10:00:00Z',
      hostStatus: 'active',
    },
    legacyAliases: ['Natalia Ruiz'],
    createdAt: '2025-08-01T10:00:00Z',
    updatedAt: '2026-06-19T10:00:00Z',
  },

  /* ── 19. Diego Ramírez — Live/Analytics host. NO confundir con Diego Montenegro ni Diego Fuentes ── */
  {
    id: 'p-019',
    firstName: 'Diego',
    lastName: 'Ramírez',
    displayName: 'Diego Ramírez',
    email: 'diego.ramirez@radiofrecuenciaweb.com',
    avatar: 'https://readdy.ai/api/search-image?query=Young%20Latino%20male%20radio%20host%20with%20trendy%20urban%20style%2C%20confident%20charismatic%20expression%2C%20modern%20studio%20environment%2C%20professional%20media%20portrait%20photography&width=160&height=160&seq=person-diego-ramirez&orientation=squarish',
    bio: 'Host nocturno. DJ de electrónica, conductor de lives y eventos especiales.',
    status: 'active',
    roles: ['host'],
    hostProfile: {
      username: '@diegoramirez',
      programs: ['noches-de-jazz'],
      followers: 16800,
      interactions: { likes: 48900, comments: 11200, shares: 9200 },
      monthlyRevenue: 1020000,
      projectedRevenue: 1250000,
      joinedAt: '2025-04-01T10:00:00Z',
      hostStatus: 'active',
    },
    legacyAliases: ['Diego Ramírez'],
    createdAt: '2025-04-01T10:00:00Z',
    updatedAt: '2026-06-19T10:00:00Z',
  },

  /* ── 20. Laura Fernández — Analytics host ── */
  {
    id: 'p-020',
    firstName: 'Laura',
    lastName: 'Fernández',
    displayName: 'Laura Fernández',
    email: 'laura.fernandez@radiofrecuenciaweb.com',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20female%20news%20anchor%20portrait%2C%20confident%20warm%20expression%2C%20elegant%20casual%20style%2C%20modern%20studio%20background%2C%20editorial%20media%20photography&width=160&height=160&seq=person-laura-fernandez&orientation=squarish',
    bio: 'Periodista y conductora de noticiero. Voz autorizada con 8 años de experiencia en medios.',
    status: 'active',
    roles: ['host'],
    hostProfile: {
      username: '@laurafernandez',
      programs: ['noticiero-central'],
      followers: 19500,
      interactions: { likes: 52300, comments: 14200, shares: 10800 },
      monthlyRevenue: 1280000,
      projectedRevenue: 1500000,
      joinedAt: '2025-01-10T10:00:00Z',
      hostStatus: 'active',
    },
    legacyAliases: ['Laura Fernández'],
    createdAt: '2025-01-10T10:00:00Z',
    updatedAt: '2026-06-19T10:00:00Z',
  },

  /* ── 21. Ana Torres — Analytics host ── */
  {
    id: 'p-021',
    firstName: 'Ana',
    lastName: 'Torres',
    displayName: 'Ana Torres',
    email: 'ana.torres@radiofrecuenciaweb.com',
    avatar: 'https://readdy.ai/api/search-image?query=Young%20Latina%20female%20radio%20host%20with%20casual%20trendy%20style%2C%20bright%20genuine%20smile%2C%20colorful%20modern%20studio%20environment%2C%20vibrant%20editorial%20portrait%20photography&width=160&height=160&seq=person-ana-torres&orientation=squarish',
    bio: 'Host de fin de semana. Especialista en música pop y programación musical variada.',
    status: 'active',
    roles: ['host'],
    hostProfile: {
      username: '@anatorres',
      programs: ['fin-de-semana-mix'],
      followers: 11200,
      interactions: { likes: 29800, comments: 7200, shares: 5400 },
      monthlyRevenue: 890000,
      projectedRevenue: 1050000,
      joinedAt: '2025-07-01T10:00:00Z',
      hostStatus: 'active',
    },
    legacyAliases: ['Ana Torres'],
    createdAt: '2025-07-01T10:00:00Z',
    updatedAt: '2026-06-19T10:00:00Z',
  },

  /* ── 22. Pablo Castillo — Analytics host ── */
  {
    id: 'p-022',
    firstName: 'Pablo',
    lastName: 'Castillo',
    displayName: 'Pablo Castillo',
    email: 'pablo.castillo@radiofrecuenciaweb.com',
    avatar: 'https://readdy.ai/api/search-image?query=Latino%20male%20sports%20radio%20host%20in%20his%20early%2030s%2C%20energetic%20passionate%20expression%2C%20casual%20sporty%20style%2C%20dynamic%20studio%20background%2C%20professional%20media%20portrait&width=160&height=160&seq=person-pablo-castillo&orientation=squarish',
    bio: 'Comentarista deportivo. Cobertura de fútbol, básquet y eventos internacionales.',
    status: 'active',
    roles: ['host'],
    hostProfile: {
      username: '@pablocastillo',
      programs: ['deportivo-express'],
      followers: 14200,
      interactions: { likes: 38700, comments: 9800, shares: 7500 },
      monthlyRevenue: 750000,
      projectedRevenue: 900000,
      joinedAt: '2025-09-01T10:00:00Z',
      hostStatus: 'active',
    },
    legacyAliases: ['Pablo Castillo'],
    createdAt: '2025-09-01T10:00:00Z',
    updatedAt: '2026-06-19T10:00:00Z',
  },
];

/* ═══════════════════════════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════════════════════════ */

export function getPersonById(id: string): CanonicalPerson | undefined {
  return canonicalPersons.find((p) => p.id === id);
}

export function getPersonByLegacyName(name: string): CanonicalPerson | undefined {
  return canonicalPersons.find((p) =>
    p.legacyAliases.some((alias) => alias.toLowerCase() === name.toLowerCase())
  );
}

export function getPersonsByRole(role: PersonRole): CanonicalPerson[] {
  return canonicalPersons.filter((p) => p.roles.includes(role));
}

export function getHosts(): CanonicalPerson[] {
  return canonicalPersons.filter((p) => p.roles.includes('host') && p.hostProfile);
}

export function getStaff(): CanonicalPerson[] {
  return canonicalPersons.filter((p) => p.staffProfile);
}